import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Clock3, MapPin } from "lucide-react";
import { SplitLines, FadeUp, Eyebrow } from "../components/Reveal";
import ContactForm from "../components/ContactForm";
import { LOCATIONS } from "../data/locations";
import { PHONE_TEL, PHONE_DISPLAY } from "../data/site";

export default function ContactPage() {
  return (
    <div data-testid="contact-page">
      <section className="relative pt-32 sm:pt-40 pb-14 overflow-hidden" data-testid="contact-hero">
        <div
          className="absolute -top-24 right-0 w-[520px] h-[520px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(255,85,0,0.16) 0%, transparent 70%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <Eyebrow>Book / Contact</Eyebrow>
          <h1 className="font-display font-extrabold uppercase leading-[0.92] text-5xl sm:text-7xl mt-4">
            <SplitLines lines={["GET A VAN", "TO YOUR DOOR."]} delay={0.25} accentLast />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.6 }}
            className="mt-5 text-zinc-400 text-lg max-w-xl"
          >
            Fill the form and it lands directly on our dispatch WhatsApp — with
            your exact location if you allow it. Or call the line: a human
            answers, 24 hours a day.
          </motion.p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20 sm:pb-28" data-testid="contact-main-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 space-y-8">
            <FadeUp>
              <div className="bg-[#131316] border border-[#27272A] p-7">
                <p className="font-telemetry text-[10px] uppercase tracking-[0.25em] text-[#FF5500] mb-4 flex items-center gap-2">
                  <Phone className="w-4 h-4" /> Dispatch line — 24/7
                </p>
                <a
                  href={PHONE_TEL}
                  data-testid="contact-page-call-button"
                  className="font-display font-extrabold uppercase text-4xl sm:text-5xl text-white hover:text-[#FF5500] transition-colors"
                >
                  {PHONE_DISPLAY}
                </a>
                <p className="mt-3 text-sm text-zinc-400">
                  Fastest option for breakdowns — we'll take your registration
                  and location and dispatch the nearest van immediately.
                </p>
              </div>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="bg-[#131316] border border-[#27272A] p-7">
                <p className="font-telemetry text-[10px] uppercase tracking-[0.25em] text-[#FF5500] mb-4 flex items-center gap-2">
                  <Clock3 className="w-4 h-4" /> Hours & response
                </p>
                <ul className="space-y-3 text-sm text-zinc-300">
                  <li className="flex justify-between border-b border-[#27272A] pb-3">
                    <span className="text-zinc-500">Opening hours</span>
                    <span className="font-telemetry uppercase tracking-wider">24 / 7 / 365</span>
                  </li>
                  <li className="flex justify-between border-b border-[#27272A] pb-3">
                    <span className="text-zinc-500">Typical response</span>
                    <span className="font-telemetry uppercase tracking-wider">30–60 minutes</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-zinc-500">Best contact</span>
                    <span className="font-telemetry uppercase tracking-wider">Call or WhatsApp</span>
                  </li>
                </ul>
                <p className="mt-5 text-xs text-zinc-500">
                  Opening hours and fees shown are sensible placeholders —
                  confirm current details when you book.
                </p>
              </div>
            </FadeUp>

            <FadeUp delay={0.15}>
              <div className="bg-[#131316] border border-[#27272A] p-7">
                <p className="font-telemetry text-[10px] uppercase tracking-[0.25em] text-[#FF5500] mb-4 flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Coverage areas
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {LOCATIONS.map((l) => (
                    <Link
                      key={l.slug}
                      to={`/areas/${l.slug}`}
                      data-testid={`contact-area-link-${l.slug}`}
                      className="text-sm text-zinc-300 hover:text-[#FF5500] transition-colors py-1.5"
                    >
                      {l.name}
                    </Link>
                  ))}
                </div>
              </div>
            </FadeUp>
          </div>

          <div className="lg:col-span-7">
            <FadeUp delay={0.1}>
              <ContactForm />
            </FadeUp>
          </div>
        </div>
      </section>
    </div>
  );
}
